import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../i18n";

export interface SectorSelectOption {
  value: string;
  label: string;
}

interface Props {
  label: string;
  options: SectorSelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  /** id suffix so multiple instances don't share a label id */
  idKey?: string;
}

/**
 * Shared Cat15 sector multi-select (chips + checkboxes + "All" placeholder).
 * Used by both the Dashboard filter bar and the Report Generate tab so the two
 * look and behave identically. Each caller supplies its own `options` (the
 * Dashboard feeds real TSIC sections; the Report feeds its report-data sectors).
 */
const SectorMultiSelect = ({
  label,
  options,
  value,
  onChange,
  idKey = "sector",
}: Props) => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });

  const optLabel = (v: string) =>
    options.find((o) => o.value === v)?.label ?? v;
  const remove = (v: string) => onChange(value.filter((x) => x !== v));

  return (
    <FormControl fullWidth size="small">
      <InputLabel id={`${idKey}-label`} shrink>
        {label}
      </InputLabel>
      <Select
        multiple
        displayEmpty
        labelId={`${idKey}-label`}
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value as unknown as string[])}
        MenuProps={{
          // Always drop below the field (never flip up and cover it) and cap
          // the height so a long sector list scrolls instead of overflowing.
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: { vertical: "top", horizontal: "left" },
          PaperProps: { sx: { maxHeight: 320 } },
        }}
        sx={{
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            pr: "32px !important",
          },
        }}
        renderValue={(selected) => {
          const arr = selected as string[];
          if (arr.length === 0) {
            // Match the sibling single-select filters, whose "All" shows in the
            // normal dark text (not a faded placeholder grey).
            return (
              <Typography variant="body1" color="var(--dark)">
                {t("Filter-All")}
              </Typography>
            );
          }
          const shown = arr.slice(0, 2);
          const extra = arr.length - shown.length;
          return (
            <Box display="flex" alignItems="center" gap="4px" overflow="hidden">
              {shown.map((s) => (
                <Chip
                  key={s}
                  size="small"
                  label={optLabel(s)}
                  onMouseDown={(e) => e.stopPropagation()}
                  onDelete={() => remove(s)}
                  sx={{
                    // pill shape to match the Overview / Total Emissions chips
                    borderRadius: "16px",
                    height: "24px",
                    minWidth: 0,
                    maxWidth: "120px",
                  }}
                />
              ))}
              {extra > 0 && (
                <Typography variant="body2" color="var(--gray1)" flexShrink={0}>
                  +{extra}
                </Typography>
              )}
            </Box>
          );
        }}
      >
        {options.map((opt) => (
          <MenuItem
            key={opt.value}
            value={opt.value}
            disableGutters
            sx={{
              px: "8px",
              "&.Mui-selected:not(.Mui-disabled)": {
                backgroundColor: "transparent",
                color: "var(--dark)",
                ".MuiListItemText-primary": { color: "var(--dark)" },
              },
              "&.Mui-selected:not(.Mui-disabled):hover": {
                backgroundColor: "var(--green3)",
              },
            }}
          >
            <Checkbox
              size="small"
              checked={value.includes(opt.value)}
              sx={{ p: "4px", mr: "4px" }}
            />
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default SectorMultiSelect;
